import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import "./style.css";

const STORAGE_KEY = "lds.userTour.v1.complete";

export const tourSteps = [
  { title: "Publishers", route: "/publisher", description: "A publisher sends leads to LDS. Create the publisher first, then open it to check its payout and channels." },
  { title: "Channels", route: "/channel", description: "A channel identifies one lead source and product. Its API credentials are issued when the channel is created; keep them private." },
  { title: "Buyers", route: "/buyers", description: "Buyers receive eligible leads. Create a buyer before adding its campaigns and request settings." },
  { title: "Campaigns", route: "/campaigns", description: "Campaigns connect a buyer to a product, price, integration, and routing rules. Open a campaign to inspect its tabs." },
  { title: "Filters", route: "/campaigns", description: "In a campaign, open Filters to choose which leads it accepts. A filtered lead is not posted to that buyer.", tab: "Filters" },
  { title: "Schedules and caps", route: "/campaigns", description: "In a campaign, open Schedule to set posting hours and caps in the campaign time zone. A closed window or reached cap skips the campaign.", tab: "Schedule" },
  { title: "Ping trees", route: "/ping-tree", description: "A ping tree orders campaigns for a channel. Open a tree to inspect tiers and the buyer walk; a channel split selects the tree." },
  { title: "Leads and logs", description: "Every lead gets a status (sold, rejected, filtered or error) and a reason code, and LDS logs each buyer attempt. The portal has no lead screen yet: staff look a lead up by its lead ID through the lead details tool (see the user guide, docs/user-guide/tour.md in lds-api)." },
];

export default function UserTour() {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== "true"; } catch { return false; }
  });
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const step = tourSteps[index];

  useEffect(() => {
    if (!active) return undefined;
    const update = () => {
      const selector = step.tab && location.pathname.startsWith("/campaigns/")
        ? `[data-tour-tab="${step.tab}"]`
        : step.route && `[data-tour-route="${step.route}"]`;
      const element = selector && document.querySelector(selector);
      setRect(element ? element.getBoundingClientRect() : null);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, location.pathname, step]);

  const close = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, "true"); } catch { /* private browsing */ }
    setActive(false);
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event) => { if (event.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close]);
  const next = () => {
    if (index === tourSteps.length - 1) close();
    else setIndex(index + 1);
  };

  return (
    <>
      <button type="button" className="tour-launch" onClick={() => { setIndex(0); setActive(true); }}>
        Take a tour
      </button>
      {/* Portal to <body>: rendered inside the AppBar (z-index 2), page content such as the
          campaigns grid painted over the card and swallowed clicks on Next. */}
      {active && createPortal(
        <div className="tour-layer" role="dialog" aria-modal="false" aria-label="LDS guided tour">
          {rect && <div className="tour-spotlight" style={{ top: Math.max(0, rect.top - 4), left: Math.max(0, rect.left - 4), width: rect.width + 8, height: rect.height + 8 }} />}
          <section className="tour-card" aria-live="polite">
            <div className="tour-counter">LDS guide · {index + 1} of {tourSteps.length}</div>
            <h2>{step.title}</h2>
            <p>{step.description}</p>
            {step.tab && !location.pathname.startsWith("/campaigns/") && <p className="tour-hint">Open a campaign to see the {step.tab} tab.</p>}
            <div className="tour-actions">
              <button type="button" onClick={close}>Dismiss</button>
              {step.route && location.pathname !== step.route && !location.pathname.startsWith(step.route + "/") && (
                <button type="button" onClick={() => navigate(step.route)}>Open screen</button>
              )}
              {index > 0 && <button type="button" onClick={() => setIndex(index - 1)}>Back</button>}
              <button type="button" className="tour-primary" onClick={next}>{index === tourSteps.length - 1 ? "Finish" : "Next"}</button>
            </div>
          </section>
        </div>,
        document.body
      )}
    </>
  );
}
